/**
 * Visits List Page
 *
 * Filter panel & chip behaviour are delegated to the shared
 * FilterPanelController (components/FilterPanelController.js). This
 * module wires page-specific pieces: cleaner/customer SearchPickers,
 * datepicker → change bridge and table sorting via hidden inputs.
 * Data fetching is HTMX.
 *
 * Action wiring (sort headers + drawer triggers in row actions) is
 * done via event delegation on document.body. Markers in the template:
 *   - [data-visits-sort="<col>"]   → sort table by column
 *   - [data-visits-action="quick-create"]               → ScheduleHub.quickCreate.show()
 *   - [data-visits-action="show-cleaner"][data-cleaner-id]   → showCleanerDrawer(id)
 *   - [data-visits-action="show-visit"|"reschedule"|"cleaner-absent"
 *      |"mark-done"|"no-show"|"cancel"|"not-performed"][data-occurrence-id]
 *      → corresponding window.show*Drawer(occId)
 *
 * Phase 6.A (2026-05-09) replaced 14 inline onclick= in
 * templates/office/visits/list.html with these markers.
 */

import { SearchPicker } from '../components/SearchPicker.js';
import FilterPanelController from '../components/FilterPanelController.js';
import {
  renderCustomerItem,
  isCustomerDisabled,
  PICKER_MODE_FILTER,
} from '../utils/ui/customer-picker.js';

let cleanerPicker = null;
let customerPicker = null;
// PR 2.2 — « Client final » picker. Mirrors the cleaner / customer
// pickers ; module-level singleton so the FilterPanelController can
// call its ``.clear()`` on Reset just like the others.
let endClientPicker = null;
let panelController = null;

// Gate the module-level htmx:afterSettle listener so it's bound
// ONCE for the SPA's lifetime and does not accumulate across
// hx-boost navigations / history restores.
let afterSettleBound = false;
let globalBound = false;
// Bulk-selection delegated listeners (« To validate » tab). Bound once
// for the SPA lifetime; the row checkboxes themselves live inside the
// HTMX-swapped #list-results, so state is refreshed on every settle.
let bulkBound = false;

export function initVisitsList(options = {}) {
  const { urls = {} } = options;

  initFilterPanel();
  initSearchPickers(urls);
  initDatepickerBridge();
  bindAfterSettleOnce();
  bindGlobalDelegation();
  initBulkSelection();
  registerQuickCreateListRefresh();
}

/**
 * Let the shared quick-create drawer (window.ScheduleHub.quickCreate)
 * refresh THIS list after a visit is created. The drawer's submit
 * pipeline (schedule-quick-create.js) calls ``refresh.list?.()`` on
 * success — on the calendar / team boards that hook is absent (they use
 * ``refresh.calendar`` / ``refresh.team`` instead), so registering it
 * here is what makes a newly-created visit show up in the office list
 * without a manual reload.
 */
function registerQuickCreateListRefresh() {
  window.ScheduleHub = window.ScheduleHub || {};
  window.ScheduleHub.refresh = window.ScheduleHub.refresh || {};
  window.ScheduleHub.refresh.list = () => {
    if (typeof window._softReload === 'function') {
      window._softReload();
    } else {
      window.location.reload();
    }
  };
}

/**
 * Auto-init entry point. Called by _runAutoInit (app.js) on every
 * htmx:afterSwap of #main-content and on htmx:historyRestore —
 * no inline <script> in the template (CSP).
 */
export function autoInitVisitsList() {
  const root = document.querySelector('[data-auto-init="visitsList"]');
  if (!root) return;
  initVisitsList({
    urls: {
      cleanerSearch: root.dataset.cleanerSearchUrl || '/office/api/cleaners/search/',
      customerSearch: root.dataset.customerSearchUrl || '/office/api/customers/search/',
      endClientSearch: root.dataset.endClientSearchUrl || '/office/api/end_clients/search/',
    },
  });
}

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  // Inner #list-results swap: app.js only re-runs Flowbite on
  // #main-content swap, so row-level tooltips/dropdowns need this.
  document.body.addEventListener('htmx:afterSettle', (evt) => {
    const target = evt.target;
    const id = target && target.id;
    if (id !== 'list-results' && id !== 'main-content') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
    // Row checkboxes were re-rendered by the swap → resync the bar.
    updateBulkState();
  });
}

// ========================================================
// Bulk validation — « To validate » tab
// ========================================================

function getCsrfToken() {
  const el = document.querySelector('#bulk-action-bar [name=csrfmiddlewaretoken]')
    || document.querySelector('[name=csrfmiddlewaretoken]');
  if (el && el.value) return el.value;
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? m[1] : '';
}

function updateBulkState() {
  const bar = document.getElementById('bulk-action-bar');
  if (!bar) return;
  const all = document.querySelectorAll('.bulk-row-checkbox');
  const checked = document.querySelectorAll('.bulk-row-checkbox:checked');
  const count = checked.length;

  bar.classList.toggle('hidden', count === 0);
  // Drop any leftover validation border once the bar is dismissed.
  if (count === 0) clearReasonError();
  const countSpan = document.getElementById('bulk-count');
  if (countSpan) countSpan.textContent = String(count);

  const selectAll = document.getElementById('bulk-select-all');
  if (selectAll) {
    selectAll.checked = all.length > 0 && count === all.length;
    selectAll.indeterminate = count > 0 && count < all.length;
  }
}

function buildBulkSummary(bar, data) {
  const fmt = (attr, n) => (bar.dataset[attr] || '').replace('{n}', String(n));
  const parts = [];
  if (data.applied) parts.push(fmt('trApplied', data.applied));
  if (data.skippedHourly) parts.push(fmt('trHourly', data.skippedHourly));
  if (data.skippedNotAllowed) parts.push(fmt('trBlocked', data.skippedNotAllowed));
  if (data.errors) parts.push(fmt('trErrors', data.errors));
  if (!parts.length) parts.push(bar.dataset.trNone || 'No visit was updated.');
  return parts.join(' · ');
}

function clearReasonError() {
  const reasonInput = document.getElementById('bulk-reason');
  if (reasonInput) reasonInput.classList.remove('form-input-invalid');
}

function submitBulkAction(action) {
  const bar = document.getElementById('bulk-action-bar');
  if (!bar) return;
  const checked = Array.from(document.querySelectorAll('.bulk-row-checkbox:checked'));
  if (!checked.length) return;

  // Parity with the per-visit drawer: « not performed » and « no-show »
  // require a documented reason; « mark done » never does. Surface the
  // miss with a red border on the field only (no toast / message).
  const reasonInput = document.getElementById('bulk-reason');
  const reasonValue = (reasonInput && reasonInput.value || '').trim();
  if ((action === 'no_show' || action === 'mark_not_performed') && !reasonValue) {
    if (reasonInput) {
      reasonInput.classList.add('form-input-invalid');
      reasonInput.focus();
    }
    return;
  }

  const confirmTpl = bar.dataset.trConfirm || '';
  const confirmMsg = confirmTpl
    ? confirmTpl.replace('{n}', String(checked.length))
    : '';
  const proceed = () => sendBulkRequest(bar, action, reasonValue, checked);

  // Use the project's custom confirm modal; fall back to the native
  // dialog only if it hasn't been wired (defensive — it is bound once
  // for the SPA lifetime from app.js).
  if (typeof window.showConfirmModal === 'function') {
    window.showConfirmModal({
      message: confirmMsg,
      type: 'info',
      onConfirm: proceed,
    });
  } else if (!confirmMsg || window.confirm(confirmMsg)) {
    proceed();
  }
}

function sendBulkRequest(bar, action, reasonValue, checked) {
  const url = bar.dataset.bulkUrl;
  const body = new URLSearchParams();
  body.append('action', action);
  body.append('reason', reasonValue);
  checked.forEach((cb) => body.append('occurrence_ids', cb.value));

  // Disable the bar's buttons while the request is in flight.
  const buttons = bar.querySelectorAll('button');
  buttons.forEach((b) => { b.disabled = true; b.classList.add('opacity-50'); });

  fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRFToken': getCsrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: body.toString(),
  })
    .then((resp) => resp.json().then((json) => ({ ok: resp.ok, json })))
    .then(({ ok, json }) => {
      if (!ok || !json || json.success !== true) {
        const msg = (json && json.error) || 'Request failed.';
        if (typeof window.showToast === 'function') window.showToast(msg, 'error');
        buttons.forEach((b) => { b.disabled = false; b.classList.remove('opacity-50'); });
        return;
      }
      const summary = buildBulkSummary(bar, {
        applied: json.applied || 0,
        skippedHourly: json.skipped_hourly || 0,
        skippedNotAllowed: json.skipped_not_allowed || 0,
        errors: json.errors || 0,
      });
      const type = json.applied > 0 ? 'success' : 'warning';
      if (typeof window.showToast === 'function') window.showToast(summary, type);
      // Reload so the list, the « To validate » badge and any freshly
      // created drafts all reflect the new state.
      window.setTimeout(() => window.location.reload(), 600);
    })
    .catch(() => {
      if (typeof window.showToast === 'function') {
        window.showToast('Request failed.', 'error');
      }
      buttons.forEach((b) => { b.disabled = false; b.classList.remove('opacity-50'); });
    });
}

export function initBulkSelection() {
  updateBulkState();

  if (bulkBound) return;
  bulkBound = true;

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || typeof t.matches !== 'function') return;
    if (t.matches('#bulk-select-all')) {
      const isChecked = t.checked;
      document.querySelectorAll('.bulk-row-checkbox').forEach((cb) => { cb.checked = isChecked; });
      updateBulkState();
    } else if (t.matches('.bulk-row-checkbox')) {
      updateBulkState();
    }
  });

  // Clear the « reason required » red border as soon as the operator
  // types in the field or clicks away from it (focusout bubbles).
  document.addEventListener('input', (e) => {
    if (e.target && e.target.id === 'bulk-reason') clearReasonError();
  });
  document.addEventListener('focusout', (e) => {
    if (e.target && e.target.id === 'bulk-reason') clearReasonError();
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    if (t.closest('#bulk-clear')) {
      document.querySelectorAll('.bulk-row-checkbox:checked').forEach((cb) => { cb.checked = false; });
      updateBulkState();
      return;
    }
    const actionBtn = t.closest('[data-bulk-action]');
    if (!actionBtn || actionBtn.disabled) return;
    submitBulkAction(actionBtn.dataset.bulkAction);
  });
}

function initFilterPanel() {
  // Singleton controller; see notes in pages/invoices.js for why we
  // re-init on every call (hx-boost replaces toggle/reset buttons).
  if (!panelController) {
    panelController = new FilterPanelController({
      formId: 'visitsFilterForm',
      virtualKeys: { date_range: ['date_from', 'date_to'] },
      // These hidden inputs carry view context, not filters: Reset must keep
      // them so "Tout réinitialiser" stays on the current tab (``view``),
      // pinned job, and ordering instead of bouncing to the full list.
      preserveFields: ['view', 'job', 'sort', 'dir'],
      pickers: [
        { name: 'cleaner', picker: () => cleanerPicker },
        { name: 'customer', picker: () => customerPicker },
        // PR 2.2 — must be registered so Reset clears the picker's
        // visible text input + the hidden id field, and so the chip
        // strip can render a removable chip for the EndClient filter.
        // The arrow accessor lets the controller resolve the picker
        // even if it was instantiated after this constructor call
        // (rare, but possible on the very first auto-init).
        { name: 'end_client', picker: () => endClientPicker },
      ],
      outsideIgnoreSelectors: '#cleaner-search-results, #customer-search-results, #end-client-search-results',
    });
  }
  panelController.init();
}

function initSearchPickers(urls) {
  const form = () => document.getElementById('visitsFilterForm');

  const cleanerInput = document.getElementById('cleaner-search');
  if (cleanerInput) {
    cleanerPicker = new SearchPicker({
      inputId: 'cleaner-search',
      hiddenInputId: 'cleaner-filter',
      dropdownId: 'cleaner-search-results',
      endpoint: urls.cleanerSearch || '/office/api/cleaners/search/',
      maxResults: 20,
      minChars: 0,
      renderItem: renderCleanerItem,
      onSelect: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
      onClear: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
    });
  }

  const customerInput = document.getElementById('customer-search');
  if (customerInput) {
    customerPicker = new SearchPicker({
      inputId: 'customer-search',
      hiddenInputId: 'customer-filter',
      dropdownId: 'customer-search-results',
      // ``include=all`` surfaces ON_HOLD + INACTIVE customers too — their
      // historical visits remain in the table so the office must be able
      // to filter on them. The shared ``renderCustomerItem`` paints a
      // status badge; both stay selectable in filter mode.
      endpoint: (urls.customerSearch || '/office/api/customers/search/') + '?include=all',
      maxResults: 20,
      minChars: 0,
      renderItem: (item) => renderCustomerItem(item, { mode: PICKER_MODE_FILTER }),
      isItemDisabled: (item) => isCustomerDisabled(item, PICKER_MODE_FILTER),
      onSelect: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
      onClear: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
    });
  }

  // PR 2.2 — EndClient picker. The input is only rendered when
  // ``subcontracting_enabled`` is True (template-gated), so an early
  // return on missing input keeps opted-out orgs silent. ``?include=all``
  // surfaces archived EndClients so an office filtering on a deactivated
  // donneur d'ordre's historical visits doesn't see an empty picker —
  // same UX rationale as the customer picker above.
  const endClientInput = document.getElementById('end-client-search');
  if (endClientInput) {
    endClientPicker = new SearchPicker({
      inputId: 'end-client-search',
      hiddenInputId: 'end-client-filter',
      dropdownId: 'end-client-search-results',
      endpoint: (urls.endClientSearch || '/office/api/end_clients/search/') + '?include=all',
      maxResults: 20,
      minChars: 0,
      renderItem: renderEndClientItem,
      onSelect: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
      onClear: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
    });
  }
}

function initDatepickerBridge() {
  document.querySelectorAll('[datepicker]').forEach((el) => {
    if (el._visDpBound) return;
    el._visDpBound = true; // property, not dataset → doesn't serialize into HTMX history snapshots
    el.addEventListener('changeDate', () => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

/**
 * Sort via hidden inputs + form submit. Called from delegated click on
 * [data-visits-sort="<col>"] markers (no inline onclick= in the
 * template — see Phase 6.A in docs/FRONTEND_AUDIT_AND_PLAN.md).
 * Still exported for backward compatibility (App.pages.sortVisitsTable).
 */
export function sortVisitsTable(column) {
  const sortInput = document.getElementById('hidden-sort');
  const dirInput = document.getElementById('hidden-dir');
  const form = document.getElementById('visitsFilterForm');
  if (!sortInput || !dirInput || !form) return;

  const currentSort = sortInput.value;
  const currentDir = dirInput.value;
  sortInput.value = column;
  dirInput.value = currentSort === column && currentDir === 'asc' ? 'desc' : 'asc';

  form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}

// ========================================================
// Delegated click — sort headers + row action buttons
// ========================================================
//
// Listeners bound ONCE per SPA lifetime (gated by globalBound). The
// delegated handler matches a small set of markers and dispatches to
// the existing window.show*Drawer helpers (and ScheduleHub.quickCreate
// for « New Visit »), which keep their global API for external call-sites.

const DRAWER_ACTION_TO_WINDOW_FN = {
  'show-visit': 'showVisitDrawer',
  'reschedule': 'showRescheduleDrawer',
  'cleaner-absent': 'showCleanerAbsentDrawer',
  'mark-done': 'showMarkDoneDrawer',
  'no-show': 'showNoShowDrawer',
  'cancel': 'showCancelDrawer',
  'not-performed': 'showNotPerformedDrawer',
};

function handleVisitsListClick(evt) {
  const t = evt.target;
  if (!t || typeof t.closest !== 'function') return;

  const sortBtn = t.closest('[data-visits-sort]');
  if (sortBtn) {
    evt.preventDefault();
    const col = sortBtn.dataset.visitsSort;
    if (col) sortVisitsTable(col);
    return;
  }

  const actionBtn = t.closest('[data-visits-action]');
  if (!actionBtn) return;

  const action = actionBtn.dataset.visitsAction;
  if (action === 'quick-create') {
    // Open the SAME customer-first quick-create drawer used by the
    // calendar / team boards (window.ScheduleHub.quickCreate), so
    // « New Visit » is consistent across the schedule and the visits
    // list. The drawer markup + bootstrap live in visits/list.html.
    const qc = window.ScheduleHub && window.ScheduleHub.quickCreate;
    if (qc && typeof qc.show === 'function') {
      qc.show();
    }
    return;
  }
  if (action === 'show-cleaner') {
    const cleanerId = actionBtn.dataset.cleanerId;
    if (cleanerId && typeof window.showCleanerDrawer === 'function') {
      window.showCleanerDrawer(cleanerId);
    }
    return;
  }
  const fnName = DRAWER_ACTION_TO_WINDOW_FN[action];
  if (!fnName) return;
  const occId = actionBtn.dataset.occurrenceId;
  if (occId && typeof window[fnName] === 'function') {
    window[fnName](occId);
  }
}

function bindGlobalDelegation() {
  if (globalBound) return;
  globalBound = true;
  document.body.addEventListener('click', handleVisitsListClick);
}

function renderCleanerItem(item) {
  const name = item.name || item.display_name;
  const initials = item.initials || getInitials(name);
  const photo = item.photo_url || item.avatar_url;

  if (photo) {
    return `
      <div class="flex items-center px-3 py-2">
        <img src="${photo}" alt="" class="w-7 h-7 rounded-full object-cover mr-2.5 flex-shrink-0">
        <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(name)}</span>
      </div>
    `;
  }

  return `
    <div class="flex items-center px-3 py-2">
      <div class="w-7 h-7 rounded-full flex items-center justify-center mr-2.5 flex-shrink-0 bg-primary-600">
        <span class="text-xs font-medium text-white">${initials}</span>
      </div>
      <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(name)}</span>
    </div>
  `;
}

// ``renderCustomerItem`` was inlined here historically — the canonical
// version now lives in ``frontend/src/utils/ui/customer-picker.js`` and is
// imported above. Kept ``getInitials`` / ``escapeHtml`` for the cleaner
// renderer below.

/**
 * PR 2.2 — Renders a single EndClient row in the « Client final »
 * filter-bar autocomplete (NOT the customer-form picker, which was
 * retired in PR 2.1f-c). Kept inline because the renderer is used by
 * exactly two pages (visits + jobs) and the markup is trivial;
 * extract to a shared helper on the 3rd consumer.
 *
 * ``item.is_active`` is exposed by the API so we can dim archived rows
 * — they stay selectable in filter mode (archived donneurs d'ordre
 * still have historical visits / jobs to look at) but are visually
 * marked so the office knows what they're filtering on.
 */
function renderEndClientItem(item) {
  const name = item.name || '';
  const dim = item.is_active === false
    ? 'opacity-60'
    : '';
  const badge = item.is_active === false
    ? ` <span class="ml-2 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Archived</span>`
    : '';
  // No leading emoji / icon — matches the customer + cleaner dropdown
  // renderers which are text-only. The picker's input icon (office
  // tower) is enough context for the user.
  return `
    <div class="flex items-center px-3 py-2 ${dim}">
      <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(name)}</span>${badge}
    </div>
  `;
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

window.initVisitsList = initVisitsList;
window.sortVisitsTable = sortVisitsTable;
window.initBulkSelection = initBulkSelection;
